const DEFAULT_TTL = 300000;
const MAX_CACHE_SIZE = 100;

const state = {
    cache: {},
    config: {
        defaultTTL: DEFAULT_TTL,
        maxCacheSize: MAX_CACHE_SIZE,
        enabled: true,
        cacheGET: true,
        cachePOST: true,
        cachePOSTEndpoints: [
            // '/cpanel/dashboard',
            // '/cpanel/shifts',
            // '/cpanel/trips',
            // '/cpanel/income',
            // '/cpanel/revenue',
        ],
        autoInvalidate: true,
    },
    stats: {
        hits: 0,
        misses: 0,
        sets: 0,
        invalidations: 0,
    },
};

const mutations = {
    SET_CACHE_ENTRY(state, { key, data, headers, ttl }) {
        if (Object.keys(state.cache).length >= state.config.maxCacheSize) {
            const oldestKey = Object.keys(state.cache).reduce((oldest, current) => {
                return state.cache[current].timestamp < state.cache[oldest].timestamp
                    ? current
                    : oldest;
            });
            delete state.cache[oldestKey];
        }

        const now = Date.now();
        const cacheTTL = ttl || state.config.defaultTTL;

        state.cache[key] = {
            data,
            headers: headers || {},
            timestamp: now,
            expiresAt: now + cacheTTL,
        };

        state.stats.sets++;
    },

    CLEAR_CACHE_ENTRY(state, key) {
        if (state.cache[key]) {
            delete state.cache[key];
            state.stats.invalidations++;
        }
    },

    CLEAR_EXPIRED_ENTRIES(state) {
        const now = Date.now();
        Object.keys(state.cache).forEach((key) => {
            if (state.cache[key].expiresAt < now) {
                delete state.cache[key];
            }
        });
    },

    CLEAR_ALL_CACHE(state) {
        state.cache = {};
        state.stats.invalidations++;
    },

    UPDATE_CACHE_CONFIG(state, config) {
        state.config = { ...state.config, ...config };
    },

    INCREMENT_HIT(state) {
        state.stats.hits++;
    },

    INCREMENT_MISS(state) {
        state.stats.misses++;
    },
};

const actions = {
    getCachedResponse({ commit, state }, key) {
        if (!state.config.enabled) {
            return null;
        }

        commit('CLEAR_EXPIRED_ENTRIES');

        const entry = state.cache[key];

        if (entry && entry.expiresAt > Date.now()) {
            commit('INCREMENT_HIT');
            return entry.data;
        }

        commit('INCREMENT_MISS');
        return null;
    },

    setCachedResponse({ commit, state }, { key, data, headers, ttl }) {
        if (!state.config.enabled) {
            return;
        }

        commit('SET_CACHE_ENTRY', { key, data, headers, ttl });
    },

    invalidateCache({ commit }, key) {
        commit('CLEAR_CACHE_ENTRY', key);
    },

    invalidateCachePattern({ commit, state }, pattern) {
        const regex = new RegExp(pattern.replace(/\*/g, '.*'));
        Object.keys(state.cache).forEach((key) => {
            if (regex.test(key)) {
                commit('CLEAR_CACHE_ENTRY', key);
            }
        });
    },

    clearExpiredCache({ commit }) {
        commit('CLEAR_EXPIRED_ENTRIES');
    },

    clearAllCache({ commit }) {
        commit('CLEAR_ALL_CACHE');
    },

    shouldFetch({ state, dispatch }, key) {
        if (!state.config.enabled) {
            return true;
        }

        dispatch('clearExpiredCache');

        const entry = state.cache[key];
        return !entry || entry.expiresAt <= Date.now();
    },
};

const getters = {
    getCacheEntry: (state) => (key) => {
        const entry = state.cache[key];
        if (!entry) {
            return null;
        }

        if (entry.expiresAt <= Date.now()) {
            return null;
        }

        return entry;
    },

    isCacheValid: (state) => (key) => {
        const entry = state.cache[key];
        return entry && entry.expiresAt > Date.now();
    },

    getCacheStats: (state) => {
        const total = state.stats.hits + state.stats.misses;
        const hitRate = total > 0 ? (state.stats.hits / total) * 100 : 0;

        return {
            size: Object.keys(state.cache).length,
            hits: state.stats.hits,
            misses: state.stats.misses,
            sets: state.stats.sets,
            invalidations: state.stats.invalidations,
            hitRate: hitRate.toFixed(2),
        };
    },

    cache: (state) => state.cache,
    config: (state) => state.config,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
